import { useEffect, useState } from "react";
import axios from "axios";
import Reveal from "@/components/Reveal";
import { MapPin, BedSingle, BedDouble, Check, MessageCircle, Home } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  available: { text: "Available Now", cls: "bg-[#2e7d32]/10 text-[#2e7d32]" },
  coming_soon: { text: "Coming Soon", cls: "bg-[#b26a00]/10 text-[#b26a00]" },
  full: { text: "Currently Full", cls: "bg-[#717171]/10 text-[#717171]" },
};

export default function Rooms() {
  const [rooms, setRooms] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/rooms`)
      .then((res) => setRooms(res.data))
      .catch(() => setRooms([]))
      .finally(() => setLoaded(true));
  }, []);

  const askAbout = (room) => {
    window.dispatchEvent(
      new CustomEvent("prefill-contact", {
        detail: `Hi, I'd like to ask about the ${room.title} in ${room.city} (${room.room_type === "private" ? "private bedroom" : "bed in a shared bedroom"}, ${room.price}/month). Is it still available?`,
      })
    );
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="rooms" className="py-20 sm:py-28 bg-white" data-testid="rooms-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="max-w-3xl">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF5A5F]">Available Rooms</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#222]">
            Current Openings
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#5a5a5a]">
            Every listing shows whether the price is for a private bedroom or for one bed in a shared
            bedroom. Availability changes often — reach out and we'll confirm what's open.
          </p>
        </Reveal>

        {loaded && rooms.length === 0 && (
          <Reveal className="mt-12">
            <div
              className="max-w-2xl mx-auto text-center bg-[#F7F7F9] border border-dashed border-[#C9C2B4] rounded-2xl px-8 py-14"
              data-testid="rooms-empty-state"
            >
              <span className="inline-flex w-14 h-14 rounded-2xl bg-[#EDE9E1]/60 items-center justify-center">
                <Home className="w-7 h-7 text-[#E0484D]" />
              </span>
              <h3 className="mt-5 text-2xl font-bold text-[#222]">No Rooms Posted Right Now</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-[#5a5a5a]">
                Openings come up regularly. Contact us about upcoming openings and we'll let you know
                as soon as a room is ready.
              </p>
              <a
                href="/#contact"
                data-testid="rooms-empty-cta"
                className="mt-6 inline-flex items-center gap-2 bg-[#FF5A5F] hover:bg-[#E0484D] text-white font-bold px-7 py-3.5 rounded-full transition-all duration-200 shadow-[0_8px_24px_rgba(255,90,95,0.35)]"
              >
                Contact Us About Upcoming Openings
              </a>
            </div>
          </Reveal>
        )}

        {rooms.length > 0 && (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {rooms.map((room, i) => {
              const status = STATUS_LABELS[room.status] || STATUS_LABELS.available;
              return (
                <Reveal key={room.id} delay={(i % 3) * 0.08}>
                  <article
                    data-testid={`room-card-${room.id}`}
                    className="h-full flex flex-col bg-[#F7F7F9] border border-[#EBEBEB] rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_24px_rgba(0,0,0,0.10)]"
                  >
                    {room.photo && (
                      <div className="h-52 overflow-hidden">
                        <img
                          src={room.photo}
                          alt={`${room.title} in ${room.city}`}
                          className="w-full h-full object-cover"
                          data-testid={`room-photo-${room.id}`}
                        />
                      </div>
                    )}
                    <div className="p-7 flex flex-col flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-lg font-bold text-[#222]">{room.title}</h3>
                          <p className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-[#717171]">
                            <MapPin className="w-4 h-4 text-[#FF5A5F]" />
                            {room.city}
                          </p>
                        </div>
                        <span className={`shrink-0 text-xs font-bold px-3 py-1.5 rounded-full ${status.cls}`}>
                          {status.text}
                        </span>
                      </div>

                      <p className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#222]">
                        {room.room_type === "private" ? (
                          <BedSingle className="w-4 h-4 text-[#FF5A5F]" />
                        ) : (
                          <BedDouble className="w-4 h-4 text-[#FF5A5F]" />
                        )}
                        {room.room_type === "private" ? "Private bedroom" : "One bed in a shared bedroom"}
                      </p>

                      <p className="mt-3 font-heading text-3xl font-extrabold text-[#222]">
                        {room.price}
                        <span className="text-sm font-semibold text-[#717171]">/month</span>
                      </p>

                      {room.included?.length > 0 && (
                        <ul className="mt-4 space-y-2">
                          {room.included.map((item) => (
                            <li key={item} className="flex items-start gap-2 text-sm text-[#444]">
                              <Check className="w-4 h-4 text-[#FF5A5F] shrink-0 mt-0.5" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}

                      <button
                        onClick={() => askAbout(room)}
                        data-testid={`room-ask-button-${room.id}`}
                        className="mt-6 inline-flex items-center justify-center gap-2 bg-[#FF5A5F] hover:bg-[#E0484D] text-white font-bold px-6 py-3 rounded-full text-sm transition-colors duration-200 w-full"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Ask About This Room
                      </button>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
